/**
 * exposureAxes — the ONE formula behind the enforcement / code-driven pair.
 *
 * Four surfaces read both axes off the same two optional wire fields, each
 * deciding which field wins, what the pre-split fallback is, and whether an
 * absent figure renders `$0`:
 *
 *   * dashboard hero          (`Dashboard/Page.tsx`)
 *   * dashboard header rail   (`Dashboard/header.ts`)
 *   * building header rail    (`BuildingDetail/header.ts`)
 *   * building Exposure board (`BuildingDetail/exposureBoard.ts`)
 *
 * Divergence is silent: a portfolio with no split would read one figure on
 * `/portal` and another on the building page it drills into.
 *
 * UNDER `components/portal`, NOT EITHER PAGE, because
 * `BuildingDetail/exposureBoard.ts` is reached from a component and a page may
 * import the component layer, never the reverse (the
 * `components/portal/caseCounts` precedent). `BuildingDetail/header.ts`
 * re-exports these four names; this module imports ONE formatter, so the eager
 * route chunks pay nothing.
 *
 * NOT MOVED: `deriveHeaderFacts`, the cell ids, the weights and `riskPillView`
 * are the BUILDING rail's presentation, and the dashboard rail draws four
 * different cells at different weights. Shared here is the ARITHMETIC and the
 * honesty rules, not the layout.
 */
import { formatNumber } from "@/utils/numberFormatters";
import { httpStatusOf } from "@/components/portal/httpStatus";

/** `28025` → `$28,025`. Exact, grouped, no abbreviation. */
export const formatUsdExact = (value: number): string =>
  `$${formatNumber(Math.round(value))}`;

/**
 * The wire fields both axes derive from. All OPTIONAL because all are optional
 * on the wire (PR-A/PR-B BE overlap, `api/portal.ts`): "the server did not
 * say" is the state these selectors keep distinct from zero.
 */
export interface ExposureAxisInput {
  /**
   * Booked city penalties (OATH all-agency + DOB/ECB/boiler).
   *
   * KNOWN DIVERGENCE: `usePenaltyExposureQuery` → `components.*.rows` (read by
   * `ExposureBoard`) is NOT floored at zero; this figure is, server-side. Close it
   * with one shared service function, never by re-deriving a rail off the rows.
   */
  enforcementExposure?: number | null;
  /** Forward statutory projection, not yet assessed. */
  codeProjectionExposure?: number | null;
  /** Blended total — the pre-split fallback, treated as enforcement. */
  penaltyExposure?: number | null;
  /** OATH snapshot missing/errored: the figure understates. */
  enforcementCoverageMissing?: boolean;
  /**
   * Row-wide penalty payload incomplete: the figure understates. DEPRECATED on
   * `PortalBuildingMetrics`, but the shipped dashboard reads it TODAY, and a
   * caveat the portfolio shows must not vanish one click later.
   */
  penaltyDataMissing?: boolean;
  /** `enforcement_fetch` (rows, codes) or `penalty_exposure.fetch`. */
  enforcementFetch?: MoneyFetch | null;
}

/**
 * F6, Leo 2026-10-02: a money source the server did not read is "Not fetched"
 * (it failed) or "Not fetched yet" (never read), never $0. `notFetched` holds
 * snake ids (array values are not camelized): ecb, oath, dob_now, boiler.
 */
export interface MoneyFetch {
  state: "success" | "failure" | "unknown";
  notFetched: string[];
}

/** `penalty_exposure.fetch` off the raw (camelized) body; the type in api/ predates it. */
export const readMoneyFetch = (block: unknown): MoneyFetch | null => {
  const f = (block as { fetch?: Partial<MoneyFetch> } | null | undefined)
    ?.fetch;
  if (!f || typeof f.state !== "string") return null;
  return {
    state: f.state,
    notFetched: Array.isArray(f.notFetched) ? f.notFetched : [],
  };
};

/** The query's fetch block. A 400 (no BIN) is null and keeps $0. Any other
 * error is the view's 502 (ECB snapshot stale, live fetch failed), so ECB is
 * named; stale `data` on an errored query is never read. */
export const moneyFetchOf = (q: {
  data?: { penaltyExposure?: unknown };
  isError: boolean;
  error?: unknown;
}): MoneyFetch | null => {
  if (!q.isError) return readMoneyFetch(q.data?.penaltyExposure);
  return httpStatusOf(q.error) === 400
    ? null
    : { state: "failure", notFetched: ["ecb"] };
};

const SOURCE_NAME: Record<string, string> = {
  ecb: "ECB",
  oath: "OATH",
  dob_now: "DOB NOW",
  boiler: "Boiler",
};

export const notFetchedLabel = (
  input: ExposureAxisInput,
): "Not fetched" | "Not fetched yet" | null => {
  const state = input.enforcementFetch?.state;
  if (state === "failure") return "Not fetched";
  if (state === "unknown") return "Not fetched yet";
  return null;
};

const isFigure = (v: number | null | undefined): v is number =>
  typeof v === "number" && Number.isFinite(v);

/**
 * Does the enforcement figure understate? ONE FORMULA, every surface.
 *
 * The dashboard's per-row `PenaltyCell` computes exactly this for exactly
 * these dollars, and the building page EXPLAINS that number, so it is the last
 * place the caveat may be quietly narrower: muted-with-a-hover on `/portal`
 * and authoritative-ink on its own page reads as the second screen correcting
 * the first.
 */
export const isExposureUnderstated = (input: ExposureAxisInput): boolean =>
  Boolean(
    input.enforcementCoverageMissing ||
      input.penaltyDataMissing ||
      notFetchedLabel(input),
  );

/** The caveat under an understated figure; names the sources not read. */
export const understatedStamp = (input: ExposureAxisInput): string | null => {
  const label = notFetchedLabel(input);
  if (label) {
    const names = (input.enforcementFetch?.notFetched ?? []).map(
      (k) => SOURCE_NAME[k] ?? k,
    );
    return `${names.join(", ") || "City record"} ${label.toLowerCase()}. This may understate.`;
  }
  return isExposureUnderstated(input)
    ? "City enforcement record incomplete. This understates."
    : null;
};

/** The figure as printed: a $0 we did not fully read prints the label. */
export const enforcementDisplay = (
  usd: number,
  input: ExposureAxisInput,
): string => (usd === 0 && notFetchedLabel(input)) || formatUsdExact(usd);

/**
 * THE ENFORCEMENT AXIS, AS A NUMBER: the split field, else the blended
 * pre-split total, else `null` for "the server did not say".
 *
 * `null` is NOT `0`. A real `0` returns `0` and renders as a figure, because
 * "no docketed enforcement" is a fact worth stating.
 */
export const selectEnforcementUsd = (
  input: ExposureAxisInput,
): number | null => {
  if (isFigure(input.enforcementExposure)) return input.enforcementExposure;
  if (isFigure(input.penaltyExposure)) return input.penaltyExposure;
  return null;
};

/**
 * THE CODE-DRIVEN AXIS, AS A NUMBER, only when the SPLIT is on the wire: under
 * the pre-split fallback those dollars are ALREADY counted as enforcement by
 * `selectEnforcementUsd`, and the boards never sum the two axes. Returning the
 * blended total would print the same dollars twice under two headings.
 */
export const selectCodeProjectionUsd = (
  input: ExposureAxisInput,
): number | null =>
  isFigure(input.codeProjectionExposure) ? input.codeProjectionExposure : null;
